import { type ClientContext, ContextClient } from '~/services/contextClient';
import GroupsClientFactory from './groupsClient.factory';
import type { GroupsClientInterface, Group } from './groupsClient.types';
import type { CursorPaginationResponse, QueryParams } from '~/services/api';
import type { AxiosPromise } from 'axios';

class GroupsClient extends ContextClient<GroupsClientInterface> {
    constructor() {
        super(GroupsClientFactory);
    }

    getGroups(
        context: ClientContext,
        params?: QueryParams,
        config?: Record<string, string | symbol>
    ): AxiosPromise<CursorPaginationResponse<Group>> {
        const client = this.getClient(context);
        return client.getGroups(params, config);
    }

    countGroups(context: ClientContext): AxiosPromise<{ count: number }> {
        const client = this.getClient(context);
        return client.countGroups();
    }

    getGroup(context: ClientContext, groupId: string) {
        const client = this.getClient(context);
        if (!client.getGroup) {
            throw new Error(`getGroup not supported by ${client.constructor.name}`);
        }
        return client.getGroup(groupId);
    }

    createGroup(context: ClientContext, data: Group) {
        const client = this.getClient(context);
        if (!client.createGroup) {
            throw new Error(`createGroup not supported by ${client.constructor.name}`);
        }
        return client.createGroup(data);
    }

    updateGroup(context: ClientContext, groupId: string, data: Group) {
        const client = this.getClient(context);
        if (!client.updateGroup) {
            throw new Error(`updateGroup not supported by ${client.constructor.name}`);
        }
        return client.updateGroup(groupId, data);
    }

    deleteGroup(context: ClientContext, groupId: string) {
        const client = this.getClient(context);
        if (!client.deleteGroup) {
            throw new Error(`deleteGroup not supported by ${client.constructor.name}`);
        }
        return client.deleteGroup(groupId);
    }

    getGroupCustomAttributes(context: ClientContext, groupId: string) {
        const client = this.getClient(context);
        if (!client.getGroupCustomAttributes) {
            throw new Error(`getGroupCustomAttributes not supported by ${client.constructor.name}`);
        }
        return client.getGroupCustomAttributes(groupId);
    }

    createGroupCustomAttribute(context: ClientContext, groupId: string, data: Group) {
        const client = this.getClient(context);
        if (!client.createGroupCustomAttribute) {
            throw new Error(`createGroupCustomAttribute not supported by ${client.constructor.name}`);
        }
        return client.createGroupCustomAttribute(groupId, data);
    }

    updateGroupCustomAttribute(
        context: ClientContext,
        groupId: string,
        customAttributeKey: string,
        data: { key: string; value: string }
    ) {
        const client = this.getClient(context);
        if (!client.updateGroupCustomAttribute) {
            throw new Error(`updateGroupCustomAttribute not supported by ${client.constructor.name}`);
        }
        return client.updateGroupCustomAttribute(groupId, customAttributeKey, data);
    }

    deleteGroupCustomAttribute(
        context: ClientContext,
        groupId: string,
        customAttributeKey: { key: string; value: string }
    ) {
        const client = this.getClient(context);
        if (!client.deleteGroupCustomAttribute) {
            throw new Error(`deleteGroupCustomAttribute not supported by ${client.constructor.name}`);
        }
        return client.deleteGroupCustomAttribute(groupId, customAttributeKey);
    }

    getGroupGroupCollections(context: ClientContext, groupId: string) {
        const client = this.getClient(context);
        if (!client.getGroupGroupCollections) {
            throw new Error(`getGroupGroupCollections not supported by ${client.constructor.name}`);
        }
        return client.getGroupGroupCollections(groupId);
    }

    /**
     * Get all custom attributes from groups that corporate or an org unit have access to
     */
    getGroupCustomAttributesMeta(context: ClientContext) {
        const client = this.getClient(context);
        if (!client.getGroupCustomAttributesMeta) {
            throw new Error(`getGroupCustomAttributesMeta not supported by ${client.constructor.name}`);
        }
        return client.getGroupCustomAttributesMeta();
    }
}

export default new GroupsClient();
